import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../services/auth.service';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';

const settings = ['Profile', 'Delete Cache', 'Logout'];

// Styled components
const ModernAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#111b21',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  borderBottom: '1px solid #e9edef',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginRight: '24px',
}));

const Logo = styled('img')(({ theme }) => ({
  width: 40,
  height: 40,
  objectFit: 'contain',
}));

const AppTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: '#111b21',
  marginLeft: '8px',
}));

const ModernButton = styled(Button)(({ theme }) => ({
  color: '#008069',
  backgroundColor: 'transparent',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  padding: '8px 16px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#e8f5e8',
    color: '#006d5a',
  },
  '&.active': {
    backgroundColor: '#008069',
    color: '#ffffff',
  },
}));

const MenuButton = styled(Button)(({ theme }) => ({
  color: '#008069',
  backgroundColor: 'transparent',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  padding: '8px 16px',
  borderRadius: '8px',
  border: '1px solid #e9edef',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#008069',
  },
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  color: '#008069',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: '#e8f5e8',
  },
  marginRight: '8px',
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  backgroundColor: '#008069',
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
    transform: 'scale(1.05)',
    transition: 'all 0.2s ease',
  },
}));

const ModernMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#ffffff',
    border: '1px solid #e9edef',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    marginTop: '8px',
  },
}));

const ModernMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '14px',
  color: '#111b21',
  padding: '12px 16px',
  '&:hover': {
    backgroundColor: '#f0f2f5',
  },
}));

const NavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const getPageLabel = (page) => {
  if (page === 'campanas_cobros') return 'Campañas de Cobros';
  if (page === 'customexcel') return 'Cargar Excel';
  if (page === 'customtable/cobros') return 'Cobros';
  if (page === 'customtable/cotizacion') return 'Cotización';
  if (page === 'customtable/cotizacionincendio') return 'Cotización Incendio';
  if (page === 'renovacion_automatizacion') return 'Automatización Renovación';
  return page.charAt(0).toUpperCase() + page.slice(1);
};

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [anchorElViews, setAnchorElViews] = React.useState(null);
  const { user, logout, currentConfig, changeCurrentConfig, getCurrentConfig } = useAuth();
  const [pages, setPages] = React.useState([]);

  const navigate = useNavigate();

  React.useEffect(() => {
    try {
      if (user) {
        if (user.roles.includes("ROLE_ADMIN")) {
          setPages(['chat', 'department', 'renovacion', 'ejecutivo', 'users', 'template', 'tag', 'customtable/cobros', 'customtable/cotizacion', 'customtable/cotizacionincendio', 'subscriptions', 'triggers', 'survey', 'renovacion_automatizacion', 'automatizaciones', 'customexcel', 'campanas_cobros', 'asegurados']);
        } else if (user.roles.includes("ROLE_USER")) {
          setPages([...new Set(user.views.map((view) => view.value))]);
        }
      }
    } catch (error) {
      //delete the user from localstorage
      localStorage.removeItem('user');
      //reload the page
      window.location.reload();
    }

  }, [user]);

  const handleOpenNavMenu = (event) => {
    //check if the page end in /chat
    if (window.location.pathname.endsWith("/chat")) {
      //back to contacts list
      changeCurrentConfig({ currentPage: "contacts", currentContact: null });
      //navigate to chat
      navigate('/dashboard/chat');
    } else {
      setAnchorElNav(event.currentTarget);
    }
  };
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenViewsMenu = (event) => {
    setAnchorElViews(event.currentTarget);
  };

  const handleCloseNavMenu = (page) => {
    //check if page is string
    if (typeof page === 'string') {
      const path = user.roles.includes("ROLE_ADMIN") ? '/admin/' : '/dashboard/';
      navigate(path + page)
      setAnchorElNav(null);
    }
    else {
      setAnchorElViews(null);
    }
  };

  const handleCloseUserMenu = (setting) => {
    if (setting === 'Logout') {
      logout();
    }
    else if (setting === 'Profile') {
      const path = user?.roles?.includes("ROLE_ADMIN") ? '/admin/settings' : '/dashboard/settings';
      navigate(path);
    }
    else if (setting === 'Delete Cache') {
      localStorage.clear();
      window.location.reload();
    }
    setAnchorElUser(null);
  };

  // Get current page to highlight active button
  const getCurrentPage = () => {
    const path = window.location.pathname;
    const page = path.split('/').pop();
    return page || 'chat';
  };

  return (
    <ModernAppBar position="static">
      <Toolbar disableGutters sx={{ padding: '8px 16px', justifyContent: 'space-between' }}>
          {/* Left Side - Mobile Navigation */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <BackButton
              size="medium"
              aria-label="back to dashboard"
              onClick={handleOpenNavMenu}
            >
              <ArrowBackIcon />
            </BackButton>
            
            <ModernMenu
              id="mobile-menu"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
            >
              {pages.map((page) => (
                <ModernMenuItem key={page} onClick={() => handleCloseNavMenu(page)}>
                  <Typography textAlign="center" sx={{ fontSize: '14px' }}>
                    {getPageLabel(page)}
                  </Typography>
                </ModernMenuItem>
              ))}
            </ModernMenu>
          </Box>

          {/* Left Side - Desktop */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Logo and Title */}
            <LogoContainer>
              <Logo 
                src={process.env.REACT_APP_APP_LOGO || "https://firebasestorage.googleapis.com/v0/b/segenesis-cotizador.appspot.com/o/notificaciones%2FGenesisLOGO%20vida%20y%20salud%20(1).png?alt=media&token=2e7ce64b-a316-4579-b3de-37468e2fa470"} 
                alt="Logo" 
              />
                          <AppTitle>
              {process.env.REACT_APP_PHONE_NAME || 'Genesis Business'}
            </AppTitle>
            </LogoContainer>

            {/* Chat Button */}
            <ModernButton
              onClick={() => handleCloseNavMenu("chat")}
              className={getCurrentPage() === 'chat' ? 'active' : ''}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              {'Chat'}
            </ModernButton>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <NavigationContainer>
                <MenuButton
                  open={Boolean(anchorElViews)}
                  aria-haspopup="true"
                  aria-expanded={Boolean(anchorElViews) ? 'true' : undefined}
                  onClick={handleOpenViewsMenu}
                  endIcon={<MenuIcon />}
                >
                  Menú
                </MenuButton>
                <ModernMenu
                  id="desktop-menu"
                  anchorEl={anchorElViews}
                  open={Boolean(anchorElViews)}
                  onClose={handleCloseNavMenu}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  {pages.map((page) => (
                    <ModernMenuItem
                      key={page}
                      onClick={() => handleCloseNavMenu(page)}
                    >
                      <Typography sx={{ fontSize: '14px' }}>
                        {getPageLabel(page)}
                      </Typography>
                    </ModernMenuItem>
                  ))}
                </ModernMenu>
              </NavigationContainer>
            </Box>
          </Box>

          {/* Right Side - User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Configuración" arrow>
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <UserAvatar>
                  {user?.username ? user.username.charAt(0).toUpperCase() : "U"}
                </UserAvatar>
              </IconButton>
            </Tooltip>
            <ModernMenu
              sx={{ mt: '45px' }}
              id="user-menu"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <ModernMenuItem key={setting} onClick={() => handleCloseUserMenu(setting)}>
                  <Typography textAlign="center" sx={{ fontSize: '14px' }}>
                    {setting}
                  </Typography>
                </ModernMenuItem>
              ))}
            </ModernMenu>
          </Box>
        </Toolbar>
    </ModernAppBar>
  );
}

export default ResponsiveAppBar;